import React, { useState } from 'react';
import { Col, Label, Button, Alert } from 'reactstrap';
import Swal from 'sweetalert2';
import { hasClauseResponse } from '../../../../shared/offer-domain/respond';
import { getOwnClauseResponse } from '../../../../shared/offer-domain/respond';
import { canRespondOnOfferDetail } from '../../utils/offerEditAccess';
import { RespondNotesEditor } from '../../../../shared/offer-domain/ui';

const MandatoryClauseBadge = () => (
  <span className='badge bg-danger-subtle text-danger border border-danger-subtle rounded-pill fw-medium fs-11'>
    Mandatory
  </span>
);

const ClauseHtmlContent = ({ value }) => {
  if (!value) {
    return <div className='text-dark mb-0 lh-base fs-13'>-</div>;
  }
  return (
    <div
      className='text-dark mb-0 lh-base fs-13'
      dangerouslySetInnerHTML={{ __html: value }}
    />
  );
};

const ClauseCard = ({
  clause,
  index,
  clauseResponses,
  draftNotes,
  expandedClauses,
  formMain,
  isClauseResponsesSaving,
  isClauseResponsesLoading,
  handleDraftChange,
  handleClauseSecondaryAction,
  handleClauseSave,
  toggleClauseExpand,
  setErrors,
  canRespond: canRespondProp,
  showHeader = true,
  showDescription = true,
}) => {
  const savedResponse = getOwnClauseResponse(clauseResponses[clause.id], {
    isEvaluator: false,
  });
  const savedNotes = savedResponse?.respondNotes ?? '';
  const hasSaved = hasClauseResponse(savedNotes);

  const isExpanded =
    expandedClauses[clause.id] !== undefined
      ? Boolean(expandedClauses[clause.id])
      : hasSaved;

  const draftValue = draftNotes[clause.id] ?? savedNotes;
  const isDraftDirty = String(draftValue ?? '') !== String(savedNotes ?? '');
  const canRespond = canRespondProp ?? canRespondOnOfferDetail(formMain);

  const [isEditing, setIsEditing] = useState(false);
  const isMandatory = clause.isShowToDMUT === true;

  return (
    <Col sm={12} className='mb-2'>
      <div
        className={`rounded-3 overflow-hidden bg-white ${
          isExpanded ? 'border border-primary shadow-sm' : 'border'
        }`}
      >
        {showHeader && (
          <div className='px-3 py-3 d-flex align-items-center gap-3 border-bottom bg-light-subtle'>
            {index != null && (
              <span
                className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 fw-semibold fs-12 ${
                  isExpanded
                    ? 'bg-primary text-white'
                    : 'bg-light text-muted border'
                }`}
                style={{ width: '28px', height: '28px' }}
              >
                {index}
              </span>
            )}
            <div className='flex-grow-1 min-w-0'>
              <div className='d-flex align-items-center gap-2 flex-wrap'>
                <span
                  className={`fs-14 mb-0 ${
                    isExpanded ? 'fw-bold text-dark' : 'fw-semibold text-dark'
                  }`}
                >
                  {clause.clauseTitle}
                </span>
                {isMandatory && <MandatoryClauseBadge />}
              </div>
            </div>
          </div>
        )}

        <div className='p-3'>
          {showDescription && isMandatory && (
            <div className='text-danger small fw-medium mb-2'>
              * Mandatory clause for DMUT to fulfill
            </div>
          )}
          {showDescription && (
            <div className='p-3 rounded-3 bg-light-subtle border text-muted fs-13 lh-base'>
              <ClauseHtmlContent value={clause.clauseDescription} />
            </div>
          )}

          <div
            className={`${showDescription ? 'border-top pt-3 mt-3 ' : ''}d-flex justify-content-between align-items-center`}
          >
            <div
              className='d-flex align-items-center gap-2'
              style={{ cursor: 'pointer' }}
              onClick={() => toggleClauseExpand(clause.id)}
            >
              <span
                className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 ${
                  isExpanded
                    ? 'bg-primary-subtle text-primary'
                    : 'bg-light text-muted'
                }`}
                style={{ width: '28px', height: '28px' }}
              >
                <i
                  className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line fs-16`}
                ></i>
              </span>
              {hasSaved ? (
                <>
                  <span
                    className='bg-danger text-white rounded-circle d-inline-flex align-items-center justify-content-center fw-semibold fs-11'
                    style={{ width: '20px', height: '20px' }}
                  >
                    !
                  </span>
                  <span className='fw-semibold text-dark fs-13'>Response</span>
                </>
              ) : (
                <span className='text-muted fs-13'>No responses yet</span>
              )}
            </div>

            {canRespond && (
              <Button
                color='light'
                size='sm'
                className='border bg-white text-muted px-3 py-1 d-flex align-items-center gap-1 fs-13 rounded-2 shadow-sm'
                onClick={() => {
                  if (!isExpanded) toggleClauseExpand(clause.id);
                  if (hasSaved) setIsEditing(true);
                }}
                disabled={isClauseResponsesLoading || hasSaved || isEditing}
              >
                <span>Respond</span>
                <i className='ri-chat-3-line fs-14 align-middle ms-1'></i>
              </Button>
            )}
          </div>

          {canRespond && isExpanded && (
            <div className='mt-3'>
              {hasSaved && !isEditing ? (
                <div className='p-3 rounded-3 border-start border-3 border-danger bg-danger-subtle mt-2'>
                  <div className='fw-bold text-dark fs-13 mb-2'>
                    Respond Notes
                  </div>
                  <div className='text-secondary fs-13 mb-3'>
                    <ClauseHtmlContent value={savedNotes} />
                  </div>

                  <div className='d-flex align-items-center gap-3'>
                    <Button
                      color='primary'
                      size='sm'
                      className='fw-medium fs-13'
                      onClick={() => setIsEditing(true)}
                    >
                      Edit
                    </Button>
                    <Button
                      color='danger'
                      size='sm'
                      className='fw-bold fs-13'
                      onClick={() => {
                        Swal.fire({
                          title: 'Are you sure?',
                          text: 'This response note will be permanently deleted.',
                          icon: 'warning',
                          showCancelButton: true,
                          confirmButtonColor: '#f1556c',
                          cancelButtonColor: '#6c757d',
                          confirmButtonText: 'Yes, Delete!',
                          cancelButtonText: 'Cancel',
                        }).then(result => {
                          if (result.isConfirmed) {
                            handleClauseSecondaryAction(clause.id, true);
                            setIsEditing(false);
                          }
                        });
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <div className='mt-2'>
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <Label className='form-label fw-semibold text-dark fs-13 mb-0'>
                      Respond Notes
                    </Label>
                    {isDraftDirty && (
                      <span className='text-warning small fs-12 fw-medium d-flex align-items-center gap-1'>
                        <i className='ri-error-warning-line fs-14'></i>
                        Unsaved Changes
                      </span>
                    )}
                  </div>

                  <RespondNotesEditor
                    id={clause.id}
                    value={draftValue}
                    onChange={e => {
                      handleDraftChange(clause.id, e.target.value);
                      if (typeof setErrors === 'function') {
                        setErrors(prev => ({ ...prev, [clause.id]: '' }));
                      }
                    }}
                    isDraftDirty={isDraftDirty}
                  />

                  <div className='d-flex gap-2 mt-3 justify-content-end'>
                    <Button
                      color='light'
                      className='border bg-white text-dark fw-semibold px-4 rounded-3'
                      onClick={() => {
                        if (hasSaved) {
                          setIsEditing(false);
                        } else {
                          toggleClauseExpand(clause.id);
                        }
                      }}
                      disabled={isClauseResponsesSaving}
                    >
                      Cancel
                    </Button>

                    <Button
                      color='primary'
                      className='fw-semibold px-4 rounded-3'
                      onClick={async () => {
                        await handleClauseSave(clause.id);
                        setIsEditing(false);
                      }}
                      disabled={isClauseResponsesSaving || !isDraftDirty}
                    >
                      {isClauseResponsesSaving ? 'Saving...' : 'Send Response'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {!canRespond && hasSaved && (
            <Alert
              className='bg-success-subtle text-success alert-solid alert-label-icon mb-0 mt-3'
              fade={false}
            >
              <i className='ri-chat-3-line label-icon'></i>
              <div>
                <div className='fw-semibold text-dark mb-1 fs-13'>
                  Respond Notes
                </div>
                <ClauseHtmlContent value={savedNotes} />
              </div>
            </Alert>
          )}
        </div>
      </div>
    </Col>
  );
};

export default ClauseCard;
